import { useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useApiClient } from '@rudi/api/react';
import { useQueryClient } from '@tanstack/react-query';
import { TeamAccessEditor } from '@rudi/ui/team-access-editor';
import { useAddTeamMember, useTenantMembers } from '../api/hooks';
import type { AdminTeam } from '../api/types';
import { TeamPreassignmentsPanel } from './TeamPreassignmentsPanel';

export function TeamMembersPanel({ tenantId, team }: { tenantId: string; team: AdminTeam }) {
  const api = useApiClient();
  const qc = useQueryClient();
  const roster = useTenantMembers(tenantId);
  const add = useAddTeamMember(tenantId, team.id);
  const request = useCallback(
    async (method: 'get' | 'post' | 'put' | 'patch' | 'delete', path: string, body?: unknown) => {
      const response =
        method === 'get' || method === 'delete'
          ? await api[method](path)
          : await api[method](path, body);
      if (method !== 'get') {
        for (const key of [
          'tenant-members',
          'team-members',
          'tenant-teams',
          'team-preassignments',
        ]) {
          void qc.invalidateQueries({ queryKey: [key, tenantId] });
        }
      }
      return response.data;
    },
    [api, qc, tenantId]
  );

  return (
    <div className="space-y-4">
      <TeamAccessEditor
        teamId={team.id}
        renderMemberLink={member => (
          <Link
            to="/users/$userId"
            params={{ userId: member.userId }}
            className="underline underline-offset-4"
            onClick={event => event.stopPropagation()}
          >
            {member.name}
          </Link>
        )}
        request={request}
        tenantAdmin
        superAdmin
        sendByDefault={false}
        readOnly={team.status !== 'active'}
        directory={{
          tenantName: roster.data?.tenant_name ?? 'this tenant',
          members: (roster.data?.members ?? []).map(member => ({
            userId: member.user_id,
            name: member.display_name ?? member.username,
            username: member.username,
            email: member.email ?? '',
          })),
          loading: roster.isLoading,
          error: !!roster.error,
          retry: () => void roster.refetch(),
          add: userId => add.mutateAsync(userId),
        }}
      />
      <details className="rounded-3xl border p-4">
        <summary className="cursor-pointer text-sm font-medium">
          Bulk preassignment and signup history
        </summary>
        <div className="mt-4">
          <TeamPreassignmentsPanel tenantId={tenantId} team={team} />
        </div>
      </details>
    </div>
  );
}
